import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import { BoxFC, BoxFR } from "components/BoxCustom"
import { initEECReportFilterData } from "./initData"
import { useCallback, useState, useContext, useMemo, useEffect } from "react"
import { Button, TextField, Paper, Typography, Box } from "@mui/material"
import { SearchRounded } from "resource/muiImport"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import dayjs from "dayjs"
import { alertError, alertWarning } from "components/Alert"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import { eecReportColumns } from "./columns"
import { getSumColorClassName, sxSumColorClass } from "utils/highlightSumColor"
import DateTimeSelect from "components/DateTimeSelect/DateTimeSelect"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import { thousandFormat } from "utils"

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels)


// Define tag names and colors (shared between charts)
const tags = [
  { name: 'BD', color: 'rgb(255, 99, 132)', bgColor: 'rgba(255, 99, 132, 0.6)' },
  { name: 'EEC', color: 'rgb(54, 162, 235)', bgColor: 'rgba(54, 162, 235, 0.6)' },
  { name: 'CTT', color: 'rgb(75, 192, 192)', bgColor: 'rgba(75, 192, 192, 0.6)' },
  { name: 'NONE', color: 'rgb(201, 203, 207)', bgColor: 'rgba(201, 203, 207, 0.6)' }
]

const EECReport = () => {
  const { ax } = useContext(GlobalStateContext)
  const [filterData, setFilterData] = useState(initEECReportFilterData)
  const [rawData, setRawData] = useState([])
  const [jobTagData, setJobTagData] = useState([])
  // const [chartDateStart, setChartDateStart] = useState(dayjs().add(-5, "month").startOf("month").format("YYYY-MM-DD 00:00:00"))
  // const [chartDateEnd, setChartDateEnd] = useState(dayjs().endOf("month").format("YYYY-MM-DD 23:59:59"))

  const monthPickerProp = {
    openTo: "month",
    views: ["year", "month"],
    disableMaskedInput: true,
    label: "เลือกเดือน",
    inputFormat: "MMMM YYYY",
    value: filterData.ScAptTm,
    onChange: (newValue) => { setFilterData(o => ({ ...o, ScAptTm: newValue })) },
    renderInput: (params) => <TextField size="small" {...params} sx={{ width: 200 }} />,
  }

  const searchEECReport = useCallback((filter) => {
    const postData = {
      ScAptTm: dayjs(filter.ScAptTm).format('YYYY-MM-01'),
      AptTmSt: dayjs(filter.AptTmSt).format('YYYY-MM-01'),
      AptTmEn: dayjs(filter.AptTmEn).format('YYYY-MM-DD 23:59:59')
    }

    ax.post('/branch/report/getEECReport', postData).then(response => {
      if (response.error) {
        alertError('เกิดข้อผิดพลาด: ' + response.error)
        return
      }

      const scMontData = response.data.scMontData
      const jobTagData = response.data.jobTagData

      if (!scMontData || scMontData.length === 0) {
        alertWarning('ไม่พบข้อมูลรถร่วม')
        setRawData([])
      } else {
        setRawData(scMontData)
      }

      if (jobTagData && jobTagData.length > 0) {
        setJobTagData(jobTagData)
      } else {
        setJobTagData([])
      }
    }).catch(error => {
      console.error('Error loading EEC report:', error)
      alertError('เกิดข้อผิดพลาดในการโหลดข้อมูล')
    })
  }, [ax])

  // Pivot data: transform driver-date rows to date rows with driver columns
  const { pivotedData, dynamicColumns } = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return { pivotedData: [], dynamicColumns: eecReportColumns }
    }

    // Get unique dates and sort them
    const dates = [...new Set(rawData.map(row => row.AptTm))].sort()

    // Get unique drivers and sort by DrvId
    const drivers = [...new Set(rawData.map(row => JSON.stringify({ DrvId: row.DrvId, DrvNm: row.DrvNm })))]
      .map(str => JSON.parse(str))
      .sort((a, b) => a.DrvId - b.DrvId)

    // Create pivoted rows
    const pivoted = dates.map((date, idx) => {
      const row = {
        id: idx + 1,
        AptTm: dayjs(date).format('DD-MMM')
      }

      let rowTotal = 0
      // Add data for each driver
      drivers.forEach(driver => {
        const dataPoint = rawData.find(r => r.AptTm === date && r.DrvId === driver.DrvId)
        const numValue = dataPoint ? dataPoint.Num : 0
        row[`drv_${driver.DrvId}`] = numValue
        rowTotal += numValue
      })

      // Add total for this row
      row.total = rowTotal

      return row
    })

    // Calculate sum row for each driver
    const sumRow = {
      id: 0,
      AptTm: 'รวมทั้งหมด'
    }
    drivers.forEach(driver => {
      sumRow[`drv_${driver.DrvId}`] = pivoted.reduce((sum, row) => sum + (row[`drv_${driver.DrvId}`] || 0), 0)
    })
    // Add total sum
    sumRow.total = pivoted.reduce((sum, row) => sum + (row.total || 0), 0)

    // Add sum row at the bottom
    const pivotedWithSum = [...pivoted, sumRow]

    // Create dynamic driver columns
    const driverCols = drivers.map(driver => ({
      field: `drv_${driver.DrvId}`,
      headerName: driver.DrvNm,
      width: 70,
      type: 'number',
      align: 'center',
      valueFormatter: (params) => {
        return params.value || 0
      },
      cellClassName: (params) => {
        if (params.id === 0) {
          return 'sum-row'
        }
        return ''
      }
    }))

    // Add total column
    const totalCol = {
      field: 'total',
      headerName: 'รวม',
      width: 80,
      type: 'number',
      align: 'right',
      valueFormatter: (params) => {
        return params.value || 0
      },
      cellClassName: (params) => {
        if (params.id === 0) {
          return 'sum-row'
        }
        return ''
      }
    }

    // Combine base columns with driver columns and total column
    const columns = [
      ...eecReportColumns,
      ...driverCols,
      totalCol
    ].map(col => {
      if (col.field === 'AptTm') {
        return {
          ...col,
          cellClassName: (params) => params.id === 0 ? 'sum-row' : ''
        }
      }
      return col
    })

    return { pivotedData: pivotedWithSum, dynamicColumns: columns }
  }, [rawData])


  // Prepare chart data from jobTagData (OwnRcptAmnt)
  const chartDataAmount = useMemo(() => {
    if (!jobTagData || jobTagData.length === 0) {
      return {
        labels: [],
        datasets: []
      }
    }

    // Get unique months and sort them
    const uniqueMonths = [...new Set(jobTagData.map(item => String(item.AptTm)))].sort()

    const labels = uniqueMonths.map(month => {
      const monthDate = dayjs(month)
      return monthDate.format('MMM YY')
    })

    // Create dataset for each tag
    const datasets = tags.map(tag => {
      const data = uniqueMonths.map(month => {
        const item = jobTagData.find(d => String(d.AptTm) === month && d.TagNm === tag.name)
        return item ? (item.OwnRcptAmnt || 0) : 0
      })

      return {
        type: 'bar',
        label: tag.name,
        data: data,
        borderColor: tag.color,
        backgroundColor: tag.bgColor,
        borderWidth: 2,
        borderRadius: 5
      }
    })

    return {
      labels,
      datasets
    }
  }, [jobTagData])

  // Prepare chart data for NumJobOrd
  const chartDataJobOrd = useMemo(() => {
    if (!jobTagData || jobTagData.length === 0) {
      return {
        labels: [],
        datasets: []
      }
    }

    // Get unique months and sort them
    const uniqueMonths = [...new Set(jobTagData.map(item => String(item.AptTm)))].sort()

    const labels = uniqueMonths.map(month => {
      const monthDate = dayjs(month)
      return monthDate.format('MMM YY')
    })

    // Create dataset for each tag
    const datasets = tags.map(tag => {
      const data = uniqueMonths.map(month => {
        const item = jobTagData.find(d => String(d.AptTm) === month && d.TagNm === tag.name)
        return item ? (item.NumJobOrd || 0) : 0
      })

      return {
        type: 'bar',
        label: tag.name,
        data: data,
        borderColor: tag.color,
        backgroundColor: tag.bgColor,
        borderWidth: 2,
        borderRadius: 5
      }
    })

    return {
      labels,
      datasets
    }
  }, [jobTagData])

  const handleSetChartDate = useCallback((start, end) => {
    setFilterData(o=>({...o, AptTmSt: start, AptTmEn: end}))
    // setChartDateStart(start)
    // setChartDateEnd(end)
  }, [])

  // Get distinct JobSNms for NONE group
  const noneGroupJobs = useMemo(() => {
    if (!jobTagData || jobTagData.length === 0) {
      return []
    }

    const noneData = jobTagData.filter(item => item.TagNm === 'NONE')
    const allJobSNms = noneData.map(item => item.JobSNms).filter(Boolean).join(',')
    const distinctJobs = [...new Set(allJobSNms.split(',').map(job => job.trim()))].filter(Boolean).sort()

    return distinctJobs
  }, [jobTagData])

  useEffect(() => {
    searchEECReport({ ...initEECReportFilterData })
  }, [searchEECReport])

  return (
    <BoxFC >

      <Paper elevation={2} sx={{ p: 2, height: 500, display: 'flex', flexDirection: 'column' }}>
        <BoxFR sx={{ alignSelf: "center", gap: 2 }}>
          <Typography variant="h6" gutterBottom>
            รายงาน จำนวน รถร่วม EEC รายวัน
          </Typography>
          <DesktopDatePicker {...monthPickerProp} />
          <Button variant="contained" onClick={() => searchEECReport(filterData)}>
            <SearchRounded sx={{ mr: 1 }} />ค้นหา
          </Button>
        </BoxFR>
        <Box
          flex={1}
          sx={sxSumColorClass}
        >
          <DataGridCellExpand
            hideFooter
            rows={pivotedData}
            columns={dynamicColumns}
            density="compact"
            disableSelectionOnClick
            experimentalFeatures={{ rowPinning: true }}
            pinnedRows={{ bottom: [pivotedData?.find(row => row.id === 0) || { id: 0 }] }}
            getRowClassName={getSumColorClassName}
          />
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BoxFR sx={{ alignSelf: "center", gap: 2, alignItems: 'center' }}>
          <Typography variant="h6">
            กราฟแสดงรายได้และจำนวนงาน
          </Typography>
          <DateTimeSelect
            label="เวลานัดส่ง"
            start={filterData.AptTmSt}
            end={filterData.AptTmEn}
            setSelectDate={handleSetChartDate}
            getData={() => searchEECReport(filterData)}
            initRadioValue="RANGE"
          />
          <Button variant="contained" onClick={() => searchEECReport(filterData)}>
            <SearchRounded sx={{ mr: 1 }} />ค้นหา
          </Button>
        </BoxFR>

        <BoxFC gap={2}>
          <Box>
            <Typography variant="subtitle1" align="center" gutterBottom>
              รายได้ (บาท)
            </Typography>
            <Box height={350}>
              <Bar
                data={chartDataAmount}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'index',
                    intersect: false
                  },
                  plugins: {
                    legend: {
                      position: 'top'
                    },
                    datalabels: {
                      display: true,
                      color: '#000',
                      font: {
                        size: 9,
                        weight: 'bold'
                      },
                      formatter: (value) => {
                        return value > 0 ? thousandFormat(value) : ''
                      },
                      anchor: 'end',
                      align: 'top'
                    }
                  },
                  scales: {
                    y: {
                      type: 'linear',
                      title: {
                        display: true,
                        text: 'รายได้ (บาท)'
                      },
                      ticks: {
                        callback: function (value) {
                          return '฿' + value.toLocaleString()
                        }
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle1" align="center" gutterBottom>
              จำนวนงาน
            </Typography>
            <Box height={350}>
              <Bar
                data={chartDataJobOrd}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: {
                    mode: 'index',
                    intersect: false
                  },
                  plugins: {
                    legend: {
                      position: 'top'
                    },
                    datalabels: {
                      display: true,
                      color: '#000',
                      font: {
                        size: 10,
                        weight: 'bold'
                      },
                      formatter: (value) => {
                        return value > 0 ? value : ''
                      },
                      anchor: 'end',
                      align: 'top'
                    }
                  },
                  scales: {
                    y: {
                      type: 'linear',
                      title: {
                        display: true,
                        text: 'จำนวนงาน'
                      },
                      ticks: {
                        stepSize: 1,
                        callback: function (value) {
                          return value
                        }
                      }
                    }
                  }
                }}
              />
            </Box>
          </Box>
        </BoxFC>

        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
            งานที่ไม่ใช่ CTT และ ไม่มี TAG ในกลุ่ม NONE ({noneGroupJobs.length} งาน)
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {noneGroupJobs.length > 0 ? noneGroupJobs.join(', ') : 'ไม่พบข้อมูล'}
          </Typography>
        </Box>
      </Paper>
    </BoxFC>
  )
}

export default EECReport